/**
 * /workforce — Lenavio Forecast.
 *
 * Three tabs (HR_ADMIN+, plan-gated by ``workforce_planning``):
 *
 *   - Current — headcount snapshot: by department, by employment
 *     type, growth/turnover, payroll roll-up.
 *   - Plans — list of headcount plans; opening one shows the
 *     planned-hires editor + an AI cost-impact projection.
 *   - Scenario — free-form what-if tool. Build a list of hypothetical
 *     hires + departures, hit "Run scenario" to get before/after
 *     numbers and an AI narrative.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
  TrendingUp,
  Users,
} from "lucide-react";

import { pageTransition } from "@/lib/utils";
import { useToast } from "@/hooks/useToast";

import {
  addHire,
  createPlan,
  deleteHire,
  deletePlan,
  fetchCurrent,
  fetchImpact,
  listPlans,
  readPlan,
  runScenario,
  updateHire,
  type HeadcountPlan,
  type HeadcountSnapshot,
  type ImpactProjection,
  type PlannedHire,
  type ScenarioDeparture,
  type ScenarioHire,
  type ScenarioResponse,
} from "@/api/workforce";

import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { Tabs, TabPanel } from "@/components/ui/Tabs";

type TabId = "current" | "plans" | "scenario";

function fmtMoney(n: number, currency = "USD"): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${currency} ${Math.round(n).toLocaleString()}`;
  }
}

function fmtPct(n: number): string {
  return `${n >= 0 ? "+" : ""}${(n * 100).toFixed(1)}%`;
}

// ════════════════════════════════════════════════════════════════════
// Current tab
// ════════════════════════════════════════════════════════════════════

function CurrentTab() {
  const { addToast } = useToast();
  const [data, setData] = useState<HeadcountSnapshot | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchCurrent();
        if (!cancelled) setData(r);
      } catch {
        if (!cancelled) addToast("error", "Failed to load snapshot");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addToast]);

  const deptMax = useMemo(
    () =>
      data
        ? Math.max(1, ...data.by_department.map((d) => d.employee_count)) * 1.1
        : 1,
    [data],
  );

  if (loading) {
    return (
      <Card className="flex h-32 items-center justify-center text-text-muted">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
      </Card>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
          <Stat label="Headcount" value={String(data.total_employees)} />
          <Stat
            label="12-mo growth"
            value={fmtPct(data.growth_rate_12m)}
            sub={`+${data.hires_last_12m} hires · −${data.departures_last_12m} departures`}
          />
          <Stat
            label="Turnover"
            value={fmtPct(data.turnover_rate_12m).replace("+", "")}
            sub="trailing 12 months"
          />
          <Stat
            label="Avg tenure"
            value={`${data.avg_tenure_years.toFixed(1)} yrs`}
          />
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <div className="mb-3 text-sm font-medium">By department</div>
          {data.by_department.length === 0 ? (
            <p className="text-sm text-text-muted">No data.</p>
          ) : (
            <div className="space-y-2">
              {data.by_department.map((d, i) => {
                const w = (d.employee_count / deptMax) * 100;
                return (
                  <div key={d.department_id || `none-${i}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span>{d.department_name || "(no department)"}</span>
                      <span className="tabular-nums">{d.employee_count}</span>
                    </div>
                    <div className="mt-1 h-2 w-full rounded-full bg-surface-muted">
                      <div
                        className="h-2 rounded-full bg-accent"
                        style={{ width: `${Math.max(2, w)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="p-5">
          <div className="mb-3 text-sm font-medium">Employment type</div>
          {data.by_employment_type.length === 0 ? (
            <p className="text-sm text-text-muted">No data.</p>
          ) : (
            <ul className="divide-y divide-border">
              {data.by_employment_type.map((e) => (
                <li
                  key={e.employment_type}
                  className="flex items-center justify-between py-2 text-sm"
                >
                  <span className="capitalize">
                    {e.employment_type.toLowerCase().replace(/_/g, " ")}
                  </span>
                  <span className="tabular-nums">{e.employee_count}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card className="p-5">
        <div className="mb-3 text-sm font-medium">Payroll roll-up</div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Stat
            label="Annual"
            value={fmtMoney(data.estimated_annual_payroll, data.currency)}
            sub="based on latest comp rows"
          />
          <Stat
            label="Monthly"
            value={fmtMoney(data.estimated_monthly_payroll, data.currency)}
          />
          <Stat
            label="Cost / employee"
            value={fmtMoney(data.cost_per_employee_annual, data.currency)}
            sub="annual"
          />
        </div>
      </Card>
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-text-muted">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-xs text-text-muted">{sub}</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Plans tab
// ════════════════════════════════════════════════════════════════════

function PlansTab() {
  const { addToast } = useToast();
  const [plans, setPlans] = useState<HeadcountPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [openPlan, setOpenPlan] = useState<HeadcountPlan | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listPlans();
      setPlans(r);
    } catch {
      addToast("error", "Failed to load plans");
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="mr-2 h-4 w-4" /> New plan
        </Button>
      </div>

      {loading ? (
        <Card className="flex h-32 items-center justify-center text-text-muted">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
        </Card>
      ) : plans.length === 0 ? (
        <Card className="p-10 text-center text-sm text-text-muted">
          No plans yet. A plan groups projected hires for a fiscal year so
          you can model their cost impact.
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {plans.map((p) => (
            <Card
              key={p.id}
              className="cursor-pointer p-5 hover:bg-surface-muted/40"
              onClick={() => setOpenPlan(p)}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold">{p.title}</h3>
                    <Badge
                      variant={
                        p.status === "active"
                          ? "success"
                          : p.status === "closed"
                            ? "default"
                            : "info"
                      }
                    >
                      {p.status}
                    </Badge>
                  </div>
                  <div className="text-sm text-text-muted">
                    FY {p.fiscal_year} · {p.hires.length} planned hire
                    {p.hires.length === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="text-right text-sm tabular-nums">
                  <div className="font-semibold">
                    {fmtMoney(p.estimated_annual_cost)}
                  </div>
                  <div className="text-xs text-text-muted">est. annual cost</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showCreate && (
        <CreatePlanModal
          onClose={() => setShowCreate(false)}
          onCreated={(p) => {
            setShowCreate(false);
            setOpenPlan(p);
            refresh();
          }}
        />
      )}
      {openPlan && (
        <PlanDetailModal
          plan={openPlan}
          onClose={() => setOpenPlan(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}

function CreatePlanModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (p: HeadcountPlan) => void;
}) {
  const { addToast } = useToast();
  const [title, setTitle] = useState("");
  const [year, setYear] = useState(new Date().getFullYear() + 1);
  const [busy, setBusy] = useState(false);

  async function onSubmit() {
    if (!title.trim()) {
      addToast("error", "Title is required");
      return;
    }
    setBusy(true);
    try {
      const p = await createPlan({
        title: title.trim(),
        fiscal_year: year,
        status: "draft",
      });
      onCreated(p);
    } catch {
      addToast("error", "Failed to create plan");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="New headcount plan">
      <div className="space-y-3">
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. FY2027 Engineering Build-Out"
        />
        <Input
          label="Fiscal year"
          type="number"
          value={String(year)}
          onChange={(e) => setYear(parseInt(e.target.value, 10) || year)}
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function PlanDetailModal({
  plan: initial,
  onClose,
  onChanged,
}: {
  plan: HeadcountPlan;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { addToast } = useToast();
  const [plan, setPlan] = useState<HeadcountPlan>(initial);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [impact, setImpact] = useState<ImpactProjection | null>(null);
  const [impactLoading, setImpactLoading] = useState(false);

  const reload = useCallback(async () => {
    try {
      const p = await readPlan(plan.id);
      setPlan(p);
      onChanged();
    } catch {
      // best-effort
    }
  }, [plan.id, onChanged]);

  async function onDeleteHire(h: PlannedHire) {
    if (!confirm(`Remove ${h.job_title}?`)) return;
    setBusy(true);
    try {
      await deleteHire(h.id);
      await reload();
    } catch {
      addToast("error", "Failed to remove hire");
    } finally {
      setBusy(false);
    }
  }

  async function onApproveHire(h: PlannedHire) {
    setBusy(true);
    try {
      await updateHire(h.id, { status: "approved" });
      await reload();
    } catch {
      addToast("error", "Failed to update hire");
    } finally {
      setBusy(false);
    }
  }

  async function onDeletePlan() {
    if (!confirm(`Delete the entire plan "${plan.title}"?`)) return;
    setBusy(true);
    try {
      await deletePlan(plan.id);
      addToast("success", "Plan deleted");
      onChanged();
      onClose();
    } catch {
      addToast("error", "Failed to delete plan");
    } finally {
      setBusy(false);
    }
  }

  async function onImpact() {
    setImpactLoading(true);
    try {
      const r = await fetchImpact(plan.id);
      setImpact(r);
    } catch {
      addToast("error", "Failed to compute impact");
    } finally {
      setImpactLoading(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={plan.title} size="xl">
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-text-muted">
            FY {plan.fiscal_year} ·{" "}
            <Badge
              variant={plan.status === "active" ? "success" : "default"}
            >
              {plan.status}
            </Badge>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onDeletePlan} disabled={busy}>
              <Trash2 className="mr-1 h-4 w-4" /> Delete plan
            </Button>
            <Button variant="ghost" onClick={() => setShowAdd(true)}>
              <Plus className="mr-1 h-4 w-4" /> Add hire
            </Button>
            <Button onClick={onImpact} disabled={impactLoading}>
              {impactLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              Run impact
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-md border border-border">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-muted text-text-muted">
              <tr>
                <th className="px-3 py-2 text-left">Title</th>
                <th className="px-3 py-2 text-left">Department</th>
                <th className="px-3 py-2 text-left">Start</th>
                <th className="px-3 py-2 text-right">Salary est.</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {plan.hires.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="py-6 text-center text-text-muted"
                  >
                    No planned hires yet.
                  </td>
                </tr>
              ) : (
                plan.hires.map((h) => (
                  <tr key={h.id} className="border-t border-border">
                    <td className="px-3 py-2 font-medium">{h.job_title}</td>
                    <td className="px-3 py-2 text-text-muted">
                      {h.department || "—"}
                    </td>
                    <td className="px-3 py-2 text-text-muted">
                      {h.target_start_date || "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {h.salary_estimate
                        ? fmtMoney(h.salary_estimate, h.currency)
                        : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <Badge
                        variant={
                          h.status === "approved"
                            ? "success"
                            : h.status === "hired"
                              ? "info"
                              : h.status === "cancelled"
                                ? "default"
                                : "warning"
                        }
                      >
                        {h.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-2">
                        {h.status === "planned" && (
                          <button
                            onClick={() => onApproveHire(h)}
                            className="text-xs text-accent hover:underline"
                          >
                            Approve
                          </button>
                        )}
                        <button
                          onClick={() => onDeleteHire(h)}
                          className="text-text-muted hover:text-danger"
                          aria-label="Remove hire"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {impact && <ImpactCard impact={impact} />}
      </div>

      {showAdd && (
        <AddHireModal
          planId={plan.id}
          onClose={() => setShowAdd(false)}
          onAdded={async () => {
            setShowAdd(false);
            await reload();
          }}
        />
      )}
    </Modal>
  );
}

function ImpactCard({ impact }: { impact: ImpactProjection }) {
  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center gap-2 text-sm font-medium">
        <TrendingUp className="h-4 w-4" /> Cost impact
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <BeforeAfter
          label="Headcount"
          before={String(impact.current_headcount)}
          after={String(impact.new_headcount)}
          delta={fmtPct(impact.headcount_growth_pct)}
        />
        <BeforeAfter
          label="Annual payroll"
          before={fmtMoney(impact.current_annual_payroll, impact.currency)}
          after={fmtMoney(impact.new_annual_payroll, impact.currency)}
          delta={fmtMoney(impact.annual_payroll_delta, impact.currency)}
        />
        <BeforeAfter
          label="Cost / employee"
          before={fmtMoney(impact.current_cost_per_employee, impact.currency)}
          after={fmtMoney(impact.new_cost_per_employee, impact.currency)}
          delta={fmtMoney(impact.cost_per_employee_delta, impact.currency)}
        />
      </div>
      {impact.ai_summary && (
        <p className="mt-4 text-sm">
          <Sparkles className="mr-1 inline h-4 w-4" /> {impact.ai_summary}
        </p>
      )}
      {impact.caveats && (
        <div className="mt-3 rounded-md border border-border bg-surface-muted p-3 text-xs text-text-muted">
          <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />{" "}
          {impact.caveats}
        </div>
      )}
    </Card>
  );
}

function BeforeAfter({
  label,
  before,
  after,
  delta,
}: {
  label: string;
  before: string;
  after: string;
  delta: string;
}) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs uppercase tracking-wide text-text-muted">
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-text-muted line-through tabular-nums">
          {before}
        </span>
        <span className="text-lg font-semibold tabular-nums">{after}</span>
      </div>
      <div className="text-xs text-accent tabular-nums">{delta}</div>
    </div>
  );
}

function AddHireModal({
  planId,
  onClose,
  onAdded,
}: {
  planId: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const { addToast } = useToast();
  const [form, setForm] = useState({
    job_title: "",
    department: "",
    target_start_date: "",
    salary_estimate: "",
    justification: "",
  });
  const [busy, setBusy] = useState(false);

  async function onSubmit() {
    if (!form.job_title.trim()) {
      addToast("error", "Job title is required");
      return;
    }
    setBusy(true);
    try {
      await addHire(planId, {
        job_title: form.job_title.trim(),
        department: form.department.trim() || null,
        target_start_date: form.target_start_date || null,
        salary_estimate: form.salary_estimate
          ? parseFloat(form.salary_estimate)
          : null,
        justification: form.justification.trim() || null,
        status: "planned",
      });
      onAdded();
    } catch {
      addToast("error", "Failed to add hire");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="Add planned hire">
      <div className="space-y-3">
        <Input
          label="Job title"
          value={form.job_title}
          onChange={(e) => setForm({ ...form, job_title: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Department"
            value={form.department}
            onChange={(e) => setForm({ ...form, department: e.target.value })}
          />
          <Input
            label="Target start"
            type="date"
            value={form.target_start_date}
            onChange={(e) =>
              setForm({ ...form, target_start_date: e.target.value })
            }
          />
        </div>
        <Input
          label="Salary estimate (annual)"
          type="number"
          step="1000"
          value={form.salary_estimate}
          onChange={(e) =>
            setForm({ ...form, salary_estimate: e.target.value })
          }
        />
        <Input
          label="Justification (optional)"
          value={form.justification}
          onChange={(e) =>
            setForm({ ...form, justification: e.target.value })
          }
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════
// Scenario tab
// ════════════════════════════════════════════════════════════════════

function ScenarioTab() {
  const { addToast } = useToast();
  const [hires, setHires] = useState<ScenarioHire[]>([
    { job_title: "", department: "", salary_estimate: undefined, count: 1 },
  ]);
  const [departures, setDepartures] = useState<ScenarioDeparture[]>([]);
  const [notes, setNotes] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<ScenarioResponse | null>(null);

  function addHireRow() {
    setHires([...hires, { job_title: "", department: "", count: 1 }]);
  }

  function addDepartureRow() {
    setDepartures([...departures, { department: "", job_title: "", count: 1 }]);
  }

  async function onRun() {
    const cleanHires = hires.filter((h) => h.job_title.trim());
    if (cleanHires.length === 0 && departures.length === 0) {
      addToast("error", "Add at least one hire or departure");
      return;
    }
    setRunning(true);
    try {
      const r = await runScenario({
        hires: cleanHires,
        departures,
        notes: notes.trim() || null,
      });
      setResult(r);
    } catch {
      addToast("error", "Scenario failed");
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Card className="p-5">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium">
          <Users className="h-4 w-4" /> Hires
        </div>
        <div className="space-y-3">
          {hires.map((h, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <input
                className="col-span-4 rounded-md border border-border bg-background px-2 py-1 text-sm"
                placeholder="Job title"
                value={h.job_title}
                onChange={(e) => {
                  const copy = [...hires];
                  copy[i] = { ...copy[i], job_title: e.target.value };
                  setHires(copy);
                }}
              />
              <input
                className="col-span-3 rounded-md border border-border bg-background px-2 py-1 text-sm"
                placeholder="Department"
                value={h.department || ""}
                onChange={(e) => {
                  const copy = [...hires];
                  copy[i] = { ...copy[i], department: e.target.value };
                  setHires(copy);
                }}
              />
              <input
                type="number"
                className="col-span-2 rounded-md border border-border bg-background px-2 py-1 text-sm"
                placeholder="Salary"
                value={h.salary_estimate ?? ""}
                onChange={(e) => {
                  const copy = [...hires];
                  copy[i] = {
                    ...copy[i],
                    salary_estimate: e.target.value
                      ? parseFloat(e.target.value)
                      : undefined,
                  };
                  setHires(copy);
                }}
              />
              <input
                type="number"
                min={1}
                className="col-span-2 rounded-md border border-border bg-background px-2 py-1 text-sm"
                placeholder="Count"
                value={h.count ?? 1}
                onChange={(e) => {
                  const copy = [...hires];
                  copy[i] = {
                    ...copy[i],
                    count: parseInt(e.target.value, 10) || 1,
                  };
                  setHires(copy);
                }}
              />
              <button
                className="col-span-1 text-text-muted hover:text-danger"
                onClick={() => setHires(hires.filter((_, j) => j !== i))}
                aria-label="Remove row"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <Button variant="ghost" size="sm" onClick={addHireRow}>
            <Plus className="mr-1 h-4 w-4" /> Add row
          </Button>
        </div>

        <div className="mt-5 mb-3 flex items-center gap-2 text-sm font-medium">
          <Users className="h-4 w-4" /> Departures
        </div>
        <div className="space-y-3">
          {departures.map((d, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <input
                className="col-span-4 rounded-md border border-border bg-background px-2 py-1 text-sm"
                placeholder="Role (optional)"
                value={d.job_title || ""}
                onChange={(e) => {
                  const copy = [...departures];
                  copy[i] = { ...copy[i], job_title: e.target.value };
                  setDepartures(copy);
                }}
              />
              <input
                className="col-span-5 rounded-md border border-border bg-background px-2 py-1 text-sm"
                placeholder="Department"
                value={d.department || ""}
                onChange={(e) => {
                  const copy = [...departures];
                  copy[i] = { ...copy[i], department: e.target.value };
                  setDepartures(copy);
                }}
              />
              <input
                type="number"
                min={1}
                className="col-span-2 rounded-md border border-border bg-background px-2 py-1 text-sm"
                placeholder="Count"
                value={d.count ?? 1}
                onChange={(e) => {
                  const copy = [...departures];
                  copy[i] = {
                    ...copy[i],
                    count: parseInt(e.target.value, 10) || 1,
                  };
                  setDepartures(copy);
                }}
              />
              <button
                className="col-span-1 text-text-muted hover:text-danger"
                onClick={() =>
                  setDepartures(departures.filter((_, j) => j !== i))
                }
                aria-label="Remove row"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <Button variant="ghost" size="sm" onClick={addDepartureRow}>
            <Plus className="mr-1 h-4 w-4" /> Add row
          </Button>
        </div>

        <textarea
          className="mt-5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          rows={3}
          placeholder="Strategic notes (optional) — e.g. 'product expansion to EU'"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        <div className="mt-4 flex justify-end">
          <Button onClick={onRun} disabled={running}>
            {running ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Modeling…
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Run scenario
              </>
            )}
          </Button>
        </div>
      </Card>

      <div className="space-y-4">
        {!result ? (
          <Card className="flex h-32 items-center justify-center text-sm text-text-muted">
            Add some hires/departures and run the scenario.
          </Card>
        ) : (
          <>
            <Card className="p-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <BeforeAfter
                  label="Headcount"
                  before={String(result.current_headcount)}
                  after={String(result.new_headcount)}
                  delta={fmtPct(result.headcount_growth_pct)}
                />
                <BeforeAfter
                  label="Monthly payroll"
                  before={fmtMoney(
                    result.current_monthly_payroll,
                    result.currency,
                  )}
                  after={fmtMoney(
                    result.new_monthly_payroll,
                    result.currency,
                  )}
                  delta={fmtMoney(
                    result.monthly_payroll_delta,
                    result.currency,
                  )}
                />
                <BeforeAfter
                  label="Cost / employee"
                  before="—"
                  after={fmtMoney(
                    result.new_cost_per_employee,
                    result.currency,
                  )}
                  delta=""
                />
              </div>
            </Card>

            {result.ai_narrative && (
              <Card className="p-5">
                <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                  <Sparkles className="h-4 w-4" /> AI narrative
                </div>
                <p className="text-sm">{result.ai_narrative}</p>
              </Card>
            )}

            {(result.risks.length > 0 || result.opportunities.length > 0) && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Card className="p-5">
                  <div className="mb-2 text-sm font-medium text-warning">
                    Risks
                  </div>
                  {result.risks.length === 0 ? (
                    <p className="text-sm text-text-muted">None flagged.</p>
                  ) : (
                    <ul className="list-disc pl-5 text-sm">
                      {result.risks.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  )}
                </Card>
                <Card className="p-5">
                  <div className="mb-2 text-sm font-medium text-success">
                    Opportunities
                  </div>
                  {result.opportunities.length === 0 ? (
                    <p className="text-sm text-text-muted">None flagged.</p>
                  ) : (
                    <ul className="list-disc pl-5 text-sm">
                      {result.opportunities.map((o, i) => (
                        <li key={i}>{o}</li>
                      ))}
                    </ul>
                  )}
                </Card>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Page shell
// ════════════════════════════════════════════════════════════════════

export function WorkforcePage() {
  const [tab, setTab] = useState<TabId>("current");
  return (
    <motion.div {...pageTransition} className="space-y-6">
      <PageHeader
        title="Workforce planning"
        subtitle="Current headcount, plan-driven hire projections, and AI-modeled scenarios."
      />
      <Tabs
        tabs={[
          { id: "current", label: "Current" },
          { id: "plans", label: "Plans" },
          { id: "scenario", label: "Scenario" },
        ]}
        activeTab={tab}
        onChange={(id) => setTab(id as TabId)}
        aria-label="Workforce sections"
      />
      <TabPanel id="current" activeTab={tab}>
        <CurrentTab />
      </TabPanel>
      <TabPanel id="plans" activeTab={tab}>
        <PlansTab />
      </TabPanel>
      <TabPanel id="scenario" activeTab={tab}>
        <ScenarioTab />
      </TabPanel>
    </motion.div>
  );
}

export default WorkforcePage;
